import { VisitorPass } from 'models/IVisitorPass';
import { AppConfig } from 'utils/constants';
import NikeStoreTealIcon from '../../assets/img/Nike-Store-Icon-Teal.png';
import NikeStoreGreyIcon from '../../assets/img/Nike-Store-Icon-Grey.png';
import SwooshTealIcon from '../../assets/img/Swoosh-Icon-Teal.png';
import SwooshGreyIcon from '../../assets/img/Swoosh-Icon-Grey.png';
import ConverseTealIcon from '../../assets/img/Converse-Star-Teal.png';
import ConverseGreyIcon from '../../assets/img/Converse-Star-Grey.png';
import ts from './i18n';
import { I18NProps } from '@nike/i18n-react/dist/mjs/nike-i18n-provider/nike-i18n-provider';
import { PassPolicy } from 'models/IPassPolicy';

export interface FamilyGridHelperProps {
  familyPolicies: PassPolicy[];
  i18nString: (key: I18NProps) => string;
  familyPasses: VisitorPass[];
  option: number;
}

export interface FamilyGridModel {
  visitorId: number;
  swooshStatus?: number;
  swooshEmail?: string;
  name: string;
  relationship: string;
  relationshipId: number;
  firstName: string;
  lastName: string;
  status: string;
  passes: VisitorPass[];
  access: Array<{
    title: string;
    src: string;
    passTypeToken: string;
    passId: number | null;
    emailAddress: string;
    statusId: number | null;
    passTypeId: number;
  }>;
  [key: string]: any;
}


//obj.statusId !== AppConfig.visitStatus.inactive && obj.statusId !== AppConfig.visitStatus.declined
export const getActiveFamilyCount = (accessAccountList: VisitorPass[]) => {
  const uniqueVisitorCount = new Set(
    accessAccountList
      .filter(
        x =>
          x.statusId !== AppConfig.passStatus.inactive &&
          x.statusId !== AppConfig.passStatus.declined,
      ) // Exclude "Inactive" and "declined" statuses
      .map(x => x.visitorId),
  ).size;

  return uniqueVisitorCount;
};

export const getActiveFamily = (data: VisitorPass[]) => {
  const activeFamilyList = data
    .filter(
      x =>
        x.statusId !== AppConfig.passStatus.inactive &&
        x.statusId !== AppConfig.passStatus.declined,
    ) // Filter out inactive and declined
    .sort((a, b) => {
      const dateA = a.statusTimestamp ? new Date(a.statusTimestamp).getTime() : 0;
      const dateB = b.statusTimestamp ? new Date(b.statusTimestamp).getTime() : 0;
      return dateB - dateA;
    }); // Sort by statusTimestamp in descending order (this will show latest family member added first)

  return activeFamilyList;
};

export const getGridForFamily = (props: FamilyGridHelperProps) => {
  let passes = [];

  switch (props.option) {
    case 0:
      passes = getActiveFamily(props.familyPasses);
      break;
    case 1:
      passes = props.familyPasses.filter(x => x.statusId === AppConfig.passStatus.inactive);
      break;
    case 2:
      passes = props.familyPasses;
      break;
    default:
      passes = getActiveFamily(props.familyPasses);
  }
  return groupByVisitorId(passes, props);
};

const groupByVisitorId = (data: VisitorPass[], props: FamilyGridHelperProps): FamilyGridModel[] => {
  const response: FamilyGridModel[] = [];

  data.forEach(item => {
    if (!item.visitorId) return;

    let visitor = response.find(v => v.visitorId === item.visitorId);

    if (!visitor) {
      visitor = {
        passes: [],
        swooshStatus: undefined, //setting inactive as default
        visitorId: item.visitorId,
        name: `${item.firstName} ${item.lastName}`,
        //When translation not found, library will return as follow ≪≪Sibling≫≫, added regex to remove leading and trailing ≪≪ and ≫≫ 
        relationship: props.i18nString(ts[item.relationshipToken as keyof typeof ts]).replace(/^≪≪|≫≫$/g, '').trim(),
        firstName: item.firstName,
        lastName: item.lastName,
        status: props.i18nString(ts['PASS_STATUS_REGISTERED' as keyof typeof ts]), //default status registered
        relationshipId: item.relationshipId ?? 0,
        passPlayIdRegistered: item.passPlayIdRegistered ?? false,
        access: createDefaultIcons(props.familyPolicies), // Predefine icons
      };
      response.push(visitor);
    }

    // Update status
    visitor.status = getPassStatus(data, item, props);
    visitor.passes?.push(item);
    // Update the corresponding icon for the current pass
    const access = visitor?.access.find(icon => icon.passTypeToken === item.passTypeToken);
    if (access) {
      switch (item.passTypeToken) {
        case 'PASS_TYPE_FAMILY':
          access.src =
            item.statusToken === 'PASS_STATUS_REGISTERED' ||
            item.statusToken === 'PASS_STATUS_PENDING'
              ? NikeStoreTealIcon
              : NikeStoreGreyIcon;
          access.emailAddress = item.emailAddress;
          access.passId = item.passId;
          access.statusId = item.statusId;
          access.passTypeId = item.passTypeId;
          break;
        case 'PASS_TYPE_SWOOSH_FAMILY':
          visitor.swooshEmail = item.emailAddress;
          visitor.swooshStatus = item.passStatusId;

          access.src =
            item.statusToken === 'PASS_STATUS_REGISTERED' ||
            item.statusToken === 'PASS_STATUS_PENDING'
              ? SwooshTealIcon
              : SwooshGreyIcon;
          access.emailAddress = item.emailAddress;
          access.passId = item.passId;
          access.statusId = item.statusId;
          access.passTypeId = item.passTypeId;
          break;
        case 'PASS_TYPE_CONVERSE_FAMILY':
          access.src =
            item.statusToken === 'PASS_STATUS_REGISTERED' ||
            item.statusToken === 'PASS_STATUS_PENDING'
              ? ConverseTealIcon
              : ConverseGreyIcon;
          access.emailAddress = item.emailAddress;
          access.passId = item.passId;
          access.statusId = item.statusId;
          access.passTypeId = item.passTypeId;
          break;
        default:
          break; // No action for unknown pass types
      }
    }
  });

  return response;
};

const getPassStatus = (
  familyPasses: VisitorPass[],
  visitor: VisitorPass,
  props: FamilyGridHelperProps,
): string => {
  const storePass = familyPasses.find(
    pass =>
      pass.visitorId === visitor.visitorId &&
      pass.passTypeToken === 'PASS_TYPE_FAMILY' &&
      (pass.statusToken === 'PASS_STATUS_REGISTERED' || pass.statusToken === 'PASS_STATUS_PENDING'),
  );

  const conversePass = familyPasses.find(
    pass =>
      pass.visitorId === visitor.visitorId &&
      pass.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY' &&
      (pass.statusToken === 'PASS_STATUS_REGISTERED' || pass.statusToken === 'PASS_STATUS_PENDING'),
  );

  const swooshPass = familyPasses.find(
    pass =>
      pass.visitorId === visitor.visitorId &&
      pass.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY' &&
      (pass.statusToken === 'PASS_STATUS_REGISTERED' || pass.statusToken === 'PASS_STATUS_PENDING'),
  );

  if (storePass) {
    return props.i18nString(ts[storePass.statusToken as keyof typeof ts]);
  } else if (conversePass) {
    return props.i18nString(ts[conversePass.statusToken as keyof typeof ts]);
  } else if (swooshPass) {
    return props.i18nString(ts[swooshPass.statusToken as keyof typeof ts]);
  } else {
    return props.i18nString(ts['PASS_STATUS_INACTIVE' as keyof typeof ts]);
  }
};

const createDefaultIcons = (
  policies: PassPolicy[],
): Array<{
  title: string;
  src: string;
  passTypeToken: string;
  passId: number | null;
  statusId: number | null;
  emailAddress: string;
  passTypeId: number;
}> => {
  return policies.map(policy => {
    let imgTitle = '';
    let imgSource = '';

    if (policy.passTypeToken === 'PASS_TYPE_FAMILY') {
      imgTitle = 'Store';
      imgSource = NikeStoreGreyIcon; // Default to grey
    } else if (policy.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY') {
      imgTitle = 'Swoosh';
      imgSource = SwooshGreyIcon; // Default to grey
    } else if (policy.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY') {
      imgTitle = 'Converse';
      imgSource = ConverseGreyIcon; // Default to grey
    }

    return {
      title: imgTitle,
      src: imgSource,
      passTypeToken: policy.passTypeToken,
      passId: null,
      emailAddress: '',
      statusId: null,
      passTypeId: policy.passTypeId,
    };
  });
};
